import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Error Handling according to instructions
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  }
  const errorJson = JSON.stringify(errInfo);
  console.error('Firestore Error: ', errorJson);
  throw new Error(errorJson);
}

// Suggestions Service
export const suggestionsService = {
  async addSuggestion(name: string, description: string, userEmail?: string) {
    const path = 'suggestions';
    try {
      await addDoc(collection(db, path), {
        toolName: name,
        description: description,
        status: 'pending',
        createdAt: serverTimestamp(),
        userEmail: userEmail || null
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  subscribeToSuggestions(callback: (suggestions: any[]) => void) {
    const path = 'suggestions';
    const q = query(collection(db, path), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const suggestions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(suggestions);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    });
  },

  async updateStatus(id: string, status: string) {
    const path = `suggestions/${id}`;
    try {
      const docRef = doc(db, 'suggestions', id);
      await updateDoc(docRef, { status });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  async deleteSuggestion(id: string) {
    const path = `suggestions/${id}`;
    try {
      const docRef = doc(db, 'suggestions', id);
      await deleteDoc(docRef);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  }
};

// Messages Service for Contact Form
export const messagesService = {
  async sendMessage(data: { name: string, email: string, subject: string, message: string }) {
    const path = 'messages';
    try {
      await addDoc(collection(db, path), {
        ...data,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  subscribeToMessages(callback: (messages: any[]) => void) {
    const path = 'messages';
    const q = query(collection(db, path), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(messages);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    });
  },

  async deleteMessage(id: string) {
    const path = `messages/${id}`;
    try {
      const docRef = doc(db, 'messages', id);
      await deleteDoc(docRef);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  }
};

// Auth Service
export const authService = {
  async signIn() {
    return signInWithPopup(auth, googleProvider);
  },
  async signOut() {
    return signOut(auth);
  }
};

// Users Service
export const usersService = {
  async saveUser(user: any) {
    if (!user) return;
    const path = `users/${user.uid}`;
    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        lastLogin: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  subscribeToUsers(callback: (users: any[]) => void) {
    const path = 'users';
    const q = query(collection(db, path), orderBy('lastLogin', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(users);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    });
  }
};

// Newsletter Service
export const newsletterService = {
  async subscribe(email: string) {
    if (!email || !email.includes('@')) throw new Error('Invalid email');
    const path = `newsletter/${email.toLowerCase()}`;
    try {
      const subscriberRef = doc(db, 'newsletter', email.toLowerCase());
      await setDoc(subscriberRef, {
        email: email.toLowerCase(),
        subscribedAt: serverTimestamp(),
        active: true
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  async unsubscribe(email: string) {
    const path = `newsletter/${email.toLowerCase()}`;
    try {
      const subscriberRef = doc(db, 'newsletter', email.toLowerCase());
      await deleteDoc(subscriberRef);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  },

  subscribeToNewsletter(callback: (subscribers: any[]) => void) {
    const path = 'newsletter';
    const q = query(collection(db, path), orderBy('subscribedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const subscribers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(subscribers);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    });
  }
};
